"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode
} from "react";
import type { Product, ProductVariation } from "@/lib/products";
import {
  addProductToCarts,
  cartDpTotalAll,
  cartSubtotal,
  cartTotalQty,
  clearProductFromCarts,
  normalizeSavedCarts,
  updateLineInCarts,
  updateNoteInCarts,
  type CartLine,
  type CartState
} from "@/lib/cartMulti";
import { cartOwnerAction, savedCartOwner, serializeOwnedCarts } from "@/lib/cartOwner";
import type { GiftClaimLine } from "@/lib/gift";
import {
  EMPTY_GIFT_CHOICES,
  giftChoicesEmpty,
  normalizeSavedGiftChoices,
  type CartGiftChoices
} from "@/lib/giftCart";
import {
  addBundleToCart,
  cartBundlesTotalPcs,
  normalizeSavedBundles,
  removeBundleFromCart,
  replaceBundleInCart,
  updateBundleQtyInCart,
  type CartBundle
} from "@/lib/bundleCart";
import { useCustomer } from "@/context/CustomerContext";

// Re-export tipe agar pemanggil lama (`@/lib/cart`) tetap kompat.
export type { CartLine, CartState } from "@/lib/cartMulti";
export type { CartBundle } from "@/lib/bundleCart";

type CartContextValue = {
  /** Semua entri produk di keranjang. */
  carts: CartState[];
  /** Entri pertama (kompat-mundur untuk pemanggil lama; null bila kosong). */
  cart: CartState | null;
  /** Total qty lintas semua produk. */
  totalQty: number;
  /** Subtotal lintas semua produk (null bila ada line tanpa harga / kosong). */
  subtotal: number | null;
  /** Jumlah entri produk di keranjang. */
  cartCount: number;
  /** Total DP semua produk (mig 070). */
  dpTotal: number;
  /**
   * `unitPrice` = harga yang DILIHAT pembeli saat menekan Tambah ke Keranjang
   * (sudah sadar promo & diskon level). Ia dipakai untuk MENAMPILKAN subtotal
   * di panel keranjang, BUKAN untuk menagih.
   *
   * Keranjang pra-checkout hidup di localStorage, jadi angka ini bisa basi:
   * kampanye promo (mig 3470) yang mulai/berakhir selagi barang menganggur di
   * keranjang tidak mengubahnya. Itu diterima sadar — `buildCheckoutLineResolver`
   * (supabase/data.ts) SELALU menghitung ulang harga dari DB saat checkout dan
   * angka itulah yang masuk `order_items.unit_price`. Yang basi paling jauh
   * hanya subtotal di layar, tak pernah nominal yang ditagih; dan menyegarkannya
   * di sini menuntut memuat ulang SEMUA produk di keranjang (mig 117 mengizinkan
   * banyak produk) tiap halaman dibuka.
   */
  addItem: (
    product: Product,
    variation: ProductVariation,
    qty: number,
    unitPrice?: number,
    /** Tambahan yang termasuk di `unitPrice` (mig 7900, bughunt D14-31). */
    priceExtra?: number
  ) => void;
  updateLine: (variationId: string, qty: number, productId?: string) => void;
  updateLineNote: (
    variationId: string,
    note: string,
    productId?: string
  ) => void;
  removeLine: (variationId: string, productId?: string) => void;
  /** Buang satu entri produk (mis. setelah checkout sukses sebagian). */
  clearProduct: (productId: string) => void;
  clearCart: () => void;
  /**
   * Keranjang tersimpan (localStorage) SUDAH dimuat. Sebelum ini `carts`
   * selalu `[]` — penulis yang menggabungkan ke keranjang lama (URL checkout
   * Meta) wajib menunggunya, kalau tidak isinya tertimpa saat pemuatan.
   */
  isReady: boolean;
  /**
   * Ganti seluruh isi keranjang dengan hasil yang sudah dihitung pemanggil
   * (`mergeMetaCheckoutIntoCarts`). Hanya untuk
   * penulis yang memegang aturan gabungnya sendiri; tombol biasa memakai
   * `addItem` / `updateLine`.
   */
  replaceCarts: (next: CartState[]) => void;
  /**
   * Bundel di keranjang (mig 8040) — larik TERPISAH dari `carts`: komponen
   * lintas produk & tak boleh melebur dengan baris biasa varian yang sama.
   */
  bundles: CartBundle[];
  addBundle: (entry: Omit<CartBundle, "key">) => void;
  /**
   * "Ubah pilihan": ganti entri `oldKey` dengan `entry` dalam SATU pembaruan
   * (`replaceBundleInCart`) — tak pernah menyisakan entri lama di samping
   * yang baru. GANTI-SAJA: `oldKey` tak ada = keranjang tak berubah (ketukan
   * ganda tak pernah menggandakan jumlah).
   */
  replaceBundle: (oldKey: string, entry: Omit<CartBundle, "key">) => void;
  updateBundleQty: (key: string, qty: number) => void;
  removeBundle: (key: string) => void;
  /** Buang semua bundel (mis. setelah checkout bundel sukses). */
  clearBundles: () => void;
  /**
   * Pilihan varian hadiah pembelian (D3) — tersimpan bersama keranjang, ikut
   * hilang saat keranjang kosong / berganti pemilik. Hadiah tak bisa dilewati
   * (H40, mig 8860).
   */
  giftChoices: CartGiftChoices;
  setGiftClaim: (ruleId: string, lines: GiftClaimLine[]) => void;
};

const CartContext = createContext<CartContextValue | undefined>(undefined);
const CART_KEY = "galea-preorder-cart";

export function CartProvider({ children }: { children: ReactNode }) {
  const [carts, setCarts] = useState<CartState[]>([]);
  const [bundles, setBundles] = useState<CartBundle[]>([]);
  const [giftChoices, setGiftChoices] = useState<CartGiftChoices>(EMPTY_GIFT_CHOICES);
  // Pelanggan pemilik isi keranjang ini (bughunt E9-8) — lihat `cartOwner.ts`.
  const [owner, setOwner] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const { customer, isLoading: customerLoading } = useCustomer();
  const customerId = customer?.id ?? null;

  // Muat keranjang tersimpan (menerima bentuk lama single & baru array).
  useEffect(() => {
    const saved = window.localStorage.getItem(CART_KEY);
    if (saved) {
      try {
        const parsed: unknown = JSON.parse(saved);
        setCarts(normalizeSavedCarts(parsed));
        setBundles(normalizeSavedBundles(parsed));
        setGiftChoices(normalizeSavedGiftChoices(parsed));
        setOwner(savedCartOwner(parsed));
      } catch {
        window.localStorage.removeItem(CART_KEY);
      }
    }
    setIsReady(true);
  }, []);

  // Keranjang milik akun LAIN tak boleh terbawa ke akun yang sedang masuk
  // (bughunt E9-8). Menunggu pelanggan termuat: selama memuat `customerId`
  // null = "tamu" → dibiarkan, bukan dikosongkan.
  useEffect(() => {
    if (!isReady || customerLoading) return;
    const action = cartOwnerAction({
      owner,
      customerId,
      hasItems: carts.length > 0 || bundles.length > 0
    });
    if (action === "clear") {
      setCarts([]);
      setBundles([]);
      setGiftChoices(EMPTY_GIFT_CHOICES);
      setOwner(null);
    } else if (action === "adopt") {
      setOwner(customerId);
    }
  }, [isReady, customerLoading, customerId, owner, carts.length, bundles.length]);

  // Persist.
  useEffect(() => {
    if (!isReady) {
      return;
    }
    if (carts.length || bundles.length) {
      window.localStorage.setItem(
        CART_KEY,
        serializeOwnedCarts(carts, owner, bundles, giftChoicesEmpty(giftChoices) ? null : giftChoices)
      );
      return;
    }
    window.localStorage.removeItem(CART_KEY);
  }, [carts, bundles, owner, giftChoices, isReady]);

  const addItem: CartContextValue["addItem"] = (
    product,
    variation,
    qty,
    unitPrice,
    priceExtra
  ) => {
    setCarts(addProductToCarts(carts, product, variation, qty, unitPrice, priceExtra));
  };

  const updateLine = (variationId: string, qty: number, productId?: string) => {
    setCarts((current) =>
      updateLineInCarts(current, productId, variationId, qty)
    );
  };

  const updateLineNote = (
    variationId: string,
    note: string,
    productId?: string
  ) => {
    setCarts((current) =>
      updateNoteInCarts(current, productId, variationId, note)
    );
  };

  const removeLine = (variationId: string, productId?: string) => {
    setCarts((current) =>
      updateLineInCarts(current, productId, variationId, 0)
    );
  };

  const clearProduct = (productId: string) => {
    setCarts((current) => clearProductFromCarts(current, productId));
  };

  const clearCart = () => {
    setCarts([]);
    setBundles([]);
    setGiftChoices(EMPTY_GIFT_CHOICES);
    // Keranjang kosong tak punya pemilik: isi berikutnya (mis. tamu sesudah
    // Keluar) tak boleh mewarisi pemilik lama lalu dikosongkan saat login.
    setOwner(null);
  };

  const replaceCarts = (next: CartState[]) => {
    setCarts(next);
  };

  const addBundle: CartContextValue["addBundle"] = (entry) => {
    setBundles((current) => addBundleToCart(current, entry));
  };
  const replaceBundle: CartContextValue["replaceBundle"] = (oldKey, entry) => {
    setBundles((current) => replaceBundleInCart(current, oldKey, entry));
  };
  const updateBundleQty = (key: string, qty: number) => {
    setBundles((current) => updateBundleQtyInCart(current, key, qty));
  };
  const removeBundle = (key: string) => {
    setBundles((current) => removeBundleFromCart(current, key));
  };
  const clearBundles = () => {
    setBundles([]);
  };
  const setGiftClaim: CartContextValue["setGiftClaim"] = (ruleId, lines) => {
    setGiftChoices((current) => ({ ...current, claims: { ...current.claims, [ruleId]: lines } }));
  };

  const value: CartContextValue = {
    carts,
    cart: carts[0] ?? null,
    // Badge jumlah barang ikut menghitung pcs bundel; subtotal & DP tetap milik
    // `carts` — bundel menampilkan harganya sendiri di kartu kelompoknya.
    totalQty: cartTotalQty(carts) + cartBundlesTotalPcs(bundles),
    subtotal: cartSubtotal(carts),
    cartCount: carts.length + bundles.length,
    dpTotal: cartDpTotalAll(carts),
    addItem,
    updateLine,
    updateLineNote,
    removeLine,
    clearProduct,
    clearCart,
    isReady,
    replaceCarts,
    bundles,
    addBundle,
    replaceBundle,
    updateBundleQty,
    removeBundle,
    clearBundles,
    giftChoices,
    setGiftClaim
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);

  if (!context) {
    throw new Error("useCart must be used inside CartProvider");
  }

  return context;
}
